import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';
import moment from 'moment';
import html2canvas from 'html2canvas';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import ReactGA from 'react-ga';

import {
  Select,
  ProgressBar,
  Button,
  LoadingSpinner,
  CircleProgressBar,
  Tabs,
} from '../../components/_shared';
import Quizzes from './components/Quizzes';
import Forum from '../Forum';
import {
  makeSelectCourses,
  makeSelectUserId,
  makeSelectUser,
  makeSelectCurrentCourse,
  makeSelectCurrentLesson,
  makeSelectCoursesLoading,
  makeSelectCurriculumIncompletedLessons,
  makeSelectCurriculumCompletePercent,
} from '../../store/selectors';
import {
  COURSE_SELECT,
  COURSE_NEXT_LESSON,
  COURSE_PREV_LESSON,
  COURSE_SELECT_LESSON,
  COURSE_COMPLETE_LESSON,
} from '../../store/actionTypes';
import { fetchCourses } from '../../store/actions/course';
import { getUserSettings, updateUserSettings } from '../../service/user';
import { createCertificate as certificateRequest } from '../../service/certificate';
import {
  replaceVideoScripts,
  notify,
  replaceOptInButton,
} from '../../utils/helper';
import { AppContext } from '../../providers/AppProvider';
import checkIco from '../../assets/imgs/check.png';
import logo from '../../assets/imgs/rabbit.png';
import './description.scss';
import './mobile.scss';
import 'react-circular-progressbar/dist/styles.css';
import {
  Content,
  Container,
  LinkButton,
  ContentTitleContainer,
  TitleContainer,
  SidebarContainer,
} from './MobileCourses.styled';

const generateImage = async () => {
  const div = document.getElementById('drawContainer');
  window.scrollTo(0, 0);
  const canvas = await html2canvas(div, {
    letterRendering: 1,
    allowTaint: false,
    useCORS: true,
  });
  const myImg = canvas.toDataURL('image/png');
  return `data:${myImg}`;
};

const tabOptions = [
  { key: 'course', label: 'Course Selection', width: 85 },
  { key: 'lesson', label: 'Lesson Content', width: 85 },
  { key: 'q&a', label: 'Q&A', width: 48 },
  // {
  //   key: 'resources',
  //   label: 'Resources',
  //   width: 70,
  // },
];

const OPTIN_LESSON_TITLE = 'Career Connections Program';

const Courses = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { curriculumId, courseId, programId } = useParams();
  const {
    loading: loadingCurriculums,
    coreCurriculum,
    endorsements,
    progress,
    fetchProgram,
  } = useContext(AppContext);
  const courses = useSelector(makeSelectCourses());
  const userId = useSelector(makeSelectUserId());
  const user = useSelector(makeSelectUser());
  const loading = useSelector(makeSelectCoursesLoading());
  const currentCourse = useSelector(makeSelectCurrentCourse(courseId));
  const currentLesson = useSelector(makeSelectCurrentLesson());
  const incompletedLessons = useSelector(
    makeSelectCurriculumIncompletedLessons()
  );
  const [, setShowQuizzes] = useState(false);
  const [showForum, setShowForum] = useState(false);
  const [tab, setTab] = useState('lesson');
  const [settings, setSettings] = useState({});
  const curriculum = [coreCurriculum, ...endorsements].find(
    (cur) => cur && cur.id === curriculumId
  );

  useEffect(() => {
    if (currentCourse && courseId !== currentCourse.id) {
      history.replace(
        `/programs/${programId}/${curriculumId}/courses/${currentCourse.id}`
      );
    }
  }, [currentCourse]);

  const percent = useMemo(() => {
    if (!curriculum) {
      return 0;
    }

    const totalPercent = curriculum.courses.reduce((p, c) => {
      const userCourse = progress.find(
        ({ course: coourse }) => coourse && coourse._id === c._id
      );

      if (userCourse) {
        const total =
          userCourse.course.lessons.length + userCourse.course.quizzes.length;
        const current =
          userCourse.completedLessons.length +
          userCourse.completedQuizzes.length;
        return p + Math.min(100, (current / total) * 100);
      } else {
        return p;
      }
    }, 0);
    return Math.floor(parseFloat(totalPercent) / curriculum.courses.length);
  }, [curriculum, progress]);

  const lessonContent = useMemo(() => {
    if (currentLesson && currentLesson.description) {
      return replaceVideoScripts(currentLesson.description);
    }

    return '';
  }, [currentLesson]);

  const handleTabChange = (val) => setTab(val);

  const handleOptIn = async () => {
    try {
      await updateUserSettings({ ...settings, optedIn: !settings.optedIn });

      // @react-ga
      ReactGA.event({
        category: 'User',
        action: 'Opted in',
      });

      notify('success', 'Updated successfully!');
      setSettings({
        ...settings,
        optedIn: !settings['optedIn'],
      });
    } catch (err) {
      notify('error', 'Failed to update: ' + (err && err.toString()));
    }
  };

  useEffect(() => {
    const optInButton = document.getElementById('opt-in-btn');
    if (optInButton) {
      optInButton.addEventListener('click', handleOptIn);
    }

    return () => {
      if (optInButton) {
        optInButton.removeEventListener('click', handleOptIn);
      }
    };
  }, [handleOptIn]);

  useEffect(() => {
    ReactGA.event({
      category: 'Content',
      action: 'Visited curriculum',
      label: curriculum?.title || curriculumId,
    });
  }, [curriculum, curriculumId]);

  useEffect(() => {
    dispatch(fetchCourses({ curriculumId }));
  }, [curriculumId]);

  useEffect(() => {
    setShowQuizzes(false);
  }, [currentCourse]);

  useEffect(() => {
    if (user && currentLesson?.title === OPTIN_LESSON_TITLE) {
      async function fetchUserSettings() {
        try {
          const res = await getUserSettings();
          setSettings(res.data.data);
        } catch (err) {
          console.log('[error.fetchusersettings]', err);
        }
      }

      fetchUserSettings();
    }
  }, [currentLesson.title]);

  const handleSelectCourse = (course) => {
    const { key, label } = course;

    ReactGA.event({
      category: 'Content',
      action: 'Selected course',
      label,
    });

    let i = 0;

    for (; i < courses.length; i += 1) {
      if (key === courses[i].id) {
        break;
      }
    }

    history.replace(`/programs/${programId}/${curriculumId}/courses/${key}`);
    dispatch({ type: COURSE_SELECT, id: i });
  };
  const handleNextLesson = () => dispatch({ type: COURSE_NEXT_LESSON });
  const handlePrevLesson = () => dispatch({ type: COURSE_PREV_LESSON });
  const handleSelectLesson = (id, title) => () => {
    ReactGA.event({
      category: 'Content',
      action: 'Selected lesson',
      label: title,
    });

    setShowQuizzes(false);
    dispatch({ type: COURSE_SELECT_LESSON, id });
    setTab('lesson');
  };
  const handleCompleteLesson = (isComplete) => async () => {
    if (isComplete) {
      ReactGA.event({
        category: 'Content',
        action: 'Completed lesson',
        label: currentLesson?.title,
      });
    }

    dispatch({ type: COURSE_COMPLETE_LESSON, isComplete });

    fetchProgram();

    if (
      incompletedLessons.length === 1 &&
      incompletedLessons[0] === currentLesson._id
    ) {
      await createCertificate();
    }

    if (isComplete) {
      handleNextLesson();
    }
  };
  const handleQuizSuccess = async () => {
    ReactGA.event({
      category: 'Content',
      action: 'Completed quiz',
      label: currentLesson?.title,
    });

    if (
      incompletedLessons.length === 1 &&
      incompletedLessons[0] === currentLesson._id
    ) {
      await createCertificate();
    }
    dispatch({ type: COURSE_COMPLETE_LESSON, isComplete: true });

    fetchProgram();

    handleNextLesson();
  };
  const handleQuizFail = () => {
    dispatch({ type: COURSE_COMPLETE_LESSON, isComplete: false });
  };
  const handleToggleForum = () => {
    setShowForum((showForum) => !showForum);
  };

  const createCertificate = async () => {
    ReactGA.event({
      category: 'User',
      action: 'Completed curriculum',
      label: curriculum?.title || curriculumId,
    });

    try {
      const image = await generateImage();

      const res = await certificateRequest({
        user: userId,
        curriculum: curriculumId,
        date: moment().format('MM-DD-yyyy'),
        image,
      });

      if (res.data.status) {
        window.open(
          `http://${window.location.host}/certification/${res.data.data.id}`,
          '_blank'
        );
      }
    } catch (err) {
      notify(
        'error',
        'Error happened in creating a certificate: ' + (err && err.toString())
      );
    }
  };

  if (loading || !currentCourse || loadingCurriculums) {
    return (
      <div className="w-full h-full m-auto max-w-6xl flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!curriculum) {
    return (
      <Container className="w-full h-full m-auto bg-transparent flex items-start overflow-hidden relative mb-9 mt-0">
        <div className="text-center">Something went wrong.</div>
      </Container>
    );
  }

  const isCore = curriculum.isCore;

  const renderLessonQuiz = (lesson, id) => (
    <div
      className={`flex items-start justify-between items-center cursor-pointer text-sm text-${
        currentLesson.id === lesson.id ? 'white font-bold selected' : 'primary'
      } p-4`}
      key={lesson.id}
      onClick={handleSelectLesson(id, lesson.title)}
    >
      <div
        className={cx(
          'mr-4 w-4 h-4 check-circle border-2 border-solid rounded-full flex items-center justify-center',
          lesson.completed && 'bg-primary border-none',
          currentLesson.id === lesson.id ? 'border-white' : 'border-primary'
        )}
      >
        {lesson.completed &&
          (currentLesson.id === lesson.id ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="8" cy="8" r="8" fill="white" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.25 6.91968L3 8.12952L6.75 11.759L13 5.70984L11.75 4.5L6.75 9.33936L4.25 6.91968Z"
                fill="#181C34"
              />
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="8" cy="8" r="8" fill="#181C34" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.25 6.91968L3 8.12952L6.75 11.759L13 5.70984L11.75 4.5L6.75 9.33936L4.25 6.91968Z"
                fill="white"
              />
            </svg>
          ))}
      </div>
      <div className="lesson-title">
        {id + 1}. {lesson.title}
      </div>
      {Array.isArray(lesson.questions) ? (
        <svg
          width="17"
          height="16"
          viewBox="0 0 17 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.24258 0C1.68562 0 1.15148 0.221249 0.757654 0.615076C0.363827 1.0089 0.142578 1.54305 0.142578 2.1V12.3C0.142578 13.46 1.08258 14.4 2.24258 14.4H7.55018C7.35018 14.088 7.18298 13.7528 7.05258 13.4H2.24258C1.95084 13.4 1.67105 13.2841 1.46476 13.0778C1.25847 12.8715 1.14258 12.5917 1.14258 12.3V2.1C1.14258 1.80826 1.25847 1.52847 1.46476 1.32218C1.67105 1.11589 1.95084 1 2.24258 1H12.4426C12.7343 1 13.0141 1.11589 13.2204 1.32218C13.4267 1.52847 13.5426 1.80826 13.5426 2.1V6.654C13.8938 6.7684 14.2286 6.9188 14.5426 7.1016V2.1C14.5426 0.94 13.6026 0 12.4426 0H2.24258Z"
            fill="black"
          />
          <path
            d="M7.55108 8.7984C7.35108 9.1104 7.18388 9.4452 7.05348 9.7984H2.24268C2.11007 9.7984 1.98289 9.74572 1.88912 9.65195C1.79535 9.55819 1.74268 9.43101 1.74268 9.2984C1.74268 9.16579 1.79535 9.03862 1.88912 8.94485C1.98289 8.85108 2.11007 8.7984 2.24268 8.7984H7.55108Z"
            fill="black"
          />
          <path
            d="M10.9417 2.09918C10.9417 1.96657 10.8891 1.8394 10.7953 1.74563C10.7015 1.65186 10.5743 1.59918 10.4417 1.59918C10.3091 1.59918 10.1819 1.65186 10.0882 1.74563C9.99441 1.8394 9.94173 1.96657 9.94173 2.09918V3.19998H8.84253C8.70992 3.19998 8.58274 3.25266 8.48898 3.34643C8.39521 3.4402 8.34253 3.56737 8.34253 3.69998C8.34253 3.83259 8.39521 3.95977 8.48898 4.05354C8.58274 4.1473 8.70992 4.19998 8.84253 4.19998H9.94173V5.29918C9.94173 5.43179 9.99441 5.55897 10.0882 5.65274C10.1819 5.7465 10.3091 5.79918 10.4417 5.79918C10.5743 5.79918 10.7015 5.7465 10.7953 5.65274C10.8891 5.55897 10.9417 5.43179 10.9417 5.29918V4.19998H12.0425C12.1751 4.19998 12.3023 4.1473 12.3961 4.05354C12.4899 3.95977 12.5425 3.83259 12.5425 3.69998C12.5425 3.56737 12.4899 3.4402 12.3961 3.34643C12.3023 3.25266 12.1751 3.19998 12.0425 3.19998H10.9417V2.09918Z"
            fill="black"
          />
          <path
            d="M6.00367 1.90841C5.96585 1.81701 5.90176 1.73889 5.81952 1.68392C5.73729 1.62895 5.64059 1.59961 5.54167 1.59961C5.44275 1.59961 5.34606 1.62895 5.26382 1.68392C5.18158 1.73889 5.1175 1.81701 5.07967 1.90841L3.18047 6.51121C3.15309 6.57229 3.13825 6.63824 3.13683 6.70515C3.13541 6.77207 3.14744 6.83859 3.17221 6.90077C3.19697 6.96295 3.23397 7.01952 3.28101 7.06714C3.32804 7.11476 3.38416 7.15244 3.44603 7.17797C3.5079 7.2035 3.57427 7.21634 3.6412 7.21574C3.70813 7.21514 3.77425 7.20111 3.83566 7.17448C3.89706 7.14785 3.9525 7.10916 3.99867 7.06071C4.04485 7.01226 4.08083 6.95503 4.10447 6.89241L4.38727 6.20721H6.69727L6.98047 6.89281C7.00477 6.9546 7.04113 7.01092 7.08743 7.0585C7.13373 7.10609 7.18904 7.14397 7.25013 7.16995C7.31123 7.19593 7.37689 7.20948 7.44328 7.20981C7.50967 7.21014 7.57546 7.19725 7.63681 7.17189C7.69816 7.14652 7.75385 7.10919 7.80062 7.06208C7.8474 7.01496 7.88432 6.959 7.90923 6.89746C7.93415 6.83593 7.94656 6.77004 7.94574 6.70366C7.94493 6.63727 7.9309 6.57172 7.90447 6.51081L6.00367 1.90841ZM6.28407 5.20721H4.80007L5.54167 3.40961L6.28407 5.20721Z"
            fill="black"
          />
          <path
            d="M1.74268 11.7C1.74268 11.424 1.96668 11.2 2.24268 11.2H5.44588C5.57848 11.2 5.70566 11.2527 5.79943 11.3465C5.8932 11.4402 5.94588 11.5674 5.94588 11.7C5.94588 11.8326 5.8932 11.9598 5.79943 12.0536C5.70566 12.1473 5.57848 12.2 5.44588 12.2H2.24268C1.96668 12.2 1.74268 11.976 1.74268 11.7Z"
            fill="black"
          />
          <path
            d="M16.3327 11.6C16.3327 14.03 14.3627 16 11.9327 16C9.50271 16 7.53271 14.03 7.53271 11.6C7.53271 9.17001 9.50271 7.20001 11.9327 7.20001C14.3627 7.20001 16.3327 9.17001 16.3327 11.6ZM12.3327 8.80001C12.3327 8.69393 12.2906 8.59218 12.2156 8.51717C12.1405 8.44215 12.0388 8.40001 11.9327 8.40001C11.8266 8.40001 11.7249 8.44215 11.6499 8.51717C11.5749 8.59218 11.5327 8.69393 11.5327 8.80001V11.2H9.13271C9.02663 11.2 8.92489 11.2422 8.84987 11.3172C8.77486 11.3922 8.73271 11.4939 8.73271 11.6C8.73271 11.7061 8.77486 11.8078 8.84987 11.8829C8.92489 11.9579 9.02663 12 9.13271 12H11.5327V14.4C11.5327 14.5061 11.5749 14.6078 11.6499 14.6829C11.7249 14.7579 11.8266 14.8 11.9327 14.8C12.0388 14.8 12.1405 14.7579 12.2156 14.6829C12.2906 14.6078 12.3327 14.5061 12.3327 14.4V12H14.7327C14.8388 12 14.9405 11.9579 15.0156 11.8829C15.0906 11.8078 15.1327 11.7061 15.1327 11.6C15.1327 11.4939 15.0906 11.3922 15.0156 11.3172C14.9405 11.2422 14.8388 11.2 14.7327 11.2H12.3327V8.80001Z"
            fill="black"
          />
        </svg>
      ) : lesson.video !== '' ? (
        <svg
          width="18"
          height="11"
          viewBox="0 0 18 11"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.7721 1.54669C17.7027 1.50751 17.6241 1.48677 17.5439 1.48652C17.4637 1.48627 17.3849 1.50653 17.3153 1.54528L13.7288 3.54237V2.82432C13.7279 2.07553 13.4223 1.35764 12.879 0.82816C12.3356 0.298679 11.5989 0.000845889 10.8305 0H1.0678C0.784698 0.000314743 0.513288 0.110044 0.313107 0.305115C0.112926 0.500186 0.000322987 0.764669 0 1.04054V8.17568C0.000868046 8.92447 0.306503 9.64236 0.849853 10.1718C1.3932 10.7013 2.12989 10.9992 2.8983 11H12.661C12.9441 10.9997 13.2155 10.89 13.4157 10.6949C13.6159 10.4998 13.7285 10.2353 13.7288 9.95946V7.45755L17.3153 9.45472C17.3849 9.49348 17.4637 9.51374 17.5439 9.51348C17.624 9.51323 17.7027 9.49246 17.772 9.45325C17.8414 9.41405 17.8989 9.35779 17.9389 9.2901C17.9789 9.22242 18 9.14568 18 9.06757V1.93243C18 1.85432 17.979 1.77757 17.939 1.70987C17.899 1.64217 17.8414 1.5859 17.7721 1.54669ZM12.8136 9.95946C12.8135 9.99887 12.7974 10.0366 12.7688 10.0645C12.7402 10.0924 12.7015 10.108 12.661 10.1081H2.8983C2.37255 10.1075 1.86849 9.90376 1.49672 9.54148C1.12496 9.1792 0.91584 8.68801 0.915254 8.17568V1.04054C0.915315 1.00113 0.931405 0.963359 0.96 0.935495C0.988594 0.907631 1.02736 0.891951 1.0678 0.891892H10.8305C11.3563 0.892462 11.8603 1.09624 12.2321 1.45852C12.6039 1.8208 12.813 2.31199 12.8136 2.82432V9.95946ZM17.0847 8.29913L13.7288 6.43047V4.56946L17.0847 2.7008V8.29913Z"
            fill="black"
          />
        </svg>
      ) : (
        <svg
          width="14"
          height="16"
          viewBox="0 0 14 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.3999 2.4V13.6C1.3999 13.9713 1.5474 14.3274 1.80995 14.5899C2.0725 14.8525 2.4286 15 2.7999 15H11.1999C11.5712 15 11.9273 14.8525 12.1899 14.5899C12.4524 14.3274 12.5999 13.9713 12.5999 13.6V5.4394C12.5999 5.25289 12.5626 5.06827 12.4902 4.89637C12.4179 4.72447 12.3119 4.56877 12.1785 4.4384L9.0705 1.399C8.80896 1.14326 8.4577 1.00005 8.0919 1H2.7999C2.4286 1 2.0725 1.1475 1.80995 1.41005C1.5474 1.6726 1.3999 2.0287 1.3999 2.4V2.4Z"
            stroke="#181C34"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.3999 1V3.8C8.3999 4.1713 8.5474 4.5274 8.80995 4.78995C9.0725 5.0525 9.4286 5.2 9.7999 5.2H12.5999"
            stroke="#181C34"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );

  const renderCompleteButton = () => (
    <Button
      className="font-semibold px-4 py-2 text-base complete-btn bg-light-yellow rounded-lg text-white font-bold"
      caption={
        currentLesson.completed ? (
          <div className="flex items-center">
            Complete
            <img className="h-2 ml-2" src={checkIco} alt="done" />
          </div>
        ) : (
          'Mark Complete'
        )
      }
      type={currentLesson.completed ? 'secondary' : 'misc'}
      onClick={handleCompleteLesson(!currentLesson.completed)}
    />
  );

  return (
    <Container className="w-full h-full m-auto bg-transparent flex items-start overflow-hidden relative mb-9 mt-0 sm:flex-col">
      <div id="drawContainer" className={isCore ? 'curriculum' : 'endorsement'}>
        {isCore && (
          <div className="content-container">
            <div className="name">{user.name}</div>
            <div className="date">{moment().format('MMM DD, YYYY')}</div>
          </div>
        )}
      </div>

      <TitleContainer className="px-6 py-9 sm:py-6 sm:px-5 mb-5 bg-primary text-yellow w-full flex justify-between items-center border border-solid border-white">
        <div className="font-bold text-white text-2xl tracking-widest uppercase title">
          {curriculum.title}
        </div>
        <div className="mt-4 progress-bar sm:mt-0 sm:flex">
          <CircularProgressbar
            value={percent}
            text={''}
            strokeWidth={5}
            styles={buildStyles({
              textColor: '#fff',
              textSize: '16px',
              pathColor: '#fff',
              trailColor: 'rgba(255, 255, 255, 0.1)',
            })}
          />
          <img className="logo" src={logo} alt="logo" />
        </div>
      </TitleContainer>

      <Content>
        {Array.isArray(currentLesson.questions) ? (
          <Quizzes
            key={currentLesson.id}
            course={currentCourse.title}
            description={currentLesson.description}
            quizId={currentLesson._id}
            quizzes={currentLesson.questions}
            onNext={handleNextLesson}
            onSuccess={handleQuizSuccess}
            onFail={handleQuizFail}
          />
        ) : (
          <ContentTitleContainer className="flex items-end justify-between">
            <div className="flex flex-col">
              <div className="text-lg text-primary font-bold uppercase flex items-center justify-between">
                {currentCourse.title}
              </div>
            </div>
          </ContentTitleContainer>
        )}

        {!showForum && (
          <>
            {currentLesson.image && (
              <img
                className="w-full rounded-lg "
                src={currentLesson.image}
                alt="video_img"
              />
            )}
            {currentLesson.video && (
              <div
                className="w-full rounded-lg "
                dangerouslySetInnerHTML={{
                  __html:
                    currentLesson.video.indexOf('[video id=') === -1
                      ? currentLesson.video
                      : replaceVideoScripts(currentLesson.video),
                }}
              />
            )}

            {!Array.isArray(currentLesson.questions) && (
              <div className="mt-4 sm:my-3 flex justify-end">
                {renderCompleteButton()}
              </div>
            )}
            <Tabs
              options={tabOptions}
              current={tab}
              onChange={handleTabChange}
            />
            {/* content wrapper */}
            {tab === 'course' && (
              <>
                <Select
                  className="border border-solid border-black rounded-lg"
                  value={{
                    key: currentCourse.id,
                    label: currentCourse.title.toUpperCase(),
                  }}
                  options={courses.map(({ id, title }, index) => ({
                    key: id,
                    label: title.toUpperCase(),
                  }))}
                  placeholder="Please select course"
                  onChange={handleSelectCourse}
                />
                <div className="lesson-list py-4 text-white flex-grow">
                  <div className="text-base font-semibold text-primary">
                    Lessons
                  </div>
                  {currentCourse?.lessonAndQuizzes.map((lesson, id) =>
                    renderLessonQuiz(lesson, id)
                  )}
                </div>
              </>
            )}
            {tab === 'lesson' && (
              <div className="w-full html-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: replaceOptInButton(lessonContent, settings.optedIn),
                  }}
                />
              </div>
            )}
            {/* {tab === 'resources' &&
              currentLesson.resources &&
              currentLesson.resources.length > 0 && (
                <ResourceContainer>
                  <div className="font-bold bg-primary text-center w-full uppercase text-white mb-3 py-2">
                    Resources
                  </div>
                  <div>
                    {currentLesson.resources.map((resource, id) => (
                      <div
                        key={resource.title + id}
                        className="mb-2 flex items-center justify-between"
                      >
                        {resource.title}
                        <a
                          href={resource.link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-dark-blue whitespace-nowrap underline"
                        >
                          <img
                            className="w-4 h-4"
                            src={downloadIco}
                            alt="download"
                          />
                        </a>
                      </div>
                    ))}
                  </div>
                </ResourceContainer>
              )}
            {tab === 'resources' &&
              (!currentLesson.resources ||
                (currentLesson.resources &&
                  currentLesson.resources.length > 0)) && (
                <div className="w-full h-32 flex items-center justify-center text-base">
                  No resources.
                </div>
              )} */}
            {tab === 'q&a' && <Forum forumId={currentCourse.id} />}
          </>
        )}
      </Content>
    </Container>
  );
};

export default Courses;
